/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { Formik } from 'formik';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps, useDispatch } from 'react-redux';

import { Button, Grid, Typography } from '@mui/material';

import { Language } from 'shared';

import { Namespaces } from '../../../../i18n';
import { AuthenticationApiService } from '../../../../services/AuthenticationApiService';
import { RootState } from '../../../../store';
import { authorizeUserByToken } from '../../../../store/auth/actions';
import { ApiClientError } from '../../../../types/api/ApiClientError';
import { UnprocessableEntityPayload } from '../../../../types/api/UnprocessableEntityPayload';
import { unprocessibleEntityToObject } from '../../../../utils/unprocessibleEntityToObject';
import { useObjectState } from '../../../../utils/useObjectState';
import { LoginPageFormValues } from '../../../LoginPage/LoginPage.types';
import { SignUpUserForm } from '../../../SignUpPage/components/SignupUserForm/SignUpUserForm';
import { validationSchema } from '../../../SignUpPage/SignUpPage.consts';
import {
  ResetApiErrorForField,
  SignUpPageApiValidationErrors,
  SignUpPageFormValues,
} from '../../../SignUpPage/SignUpPage.types';
import { useSetupPageStyles } from '../../SetupPage.styles';

const mapState = (state: RootState) => ({
  auth: state.auth,
});

const connector = connect(mapState);
type PropsFromRedux = ConnectedProps<typeof connector>;

type SuperuserStepProps = {
  onUserCreate: () => void;
} & PropsFromRedux;

type State = {
  isFetching: boolean;
  errorMessage?: string;
  values: SignUpPageFormValues;
  apiErrors: SignUpPageApiValidationErrors;
};

const SuperuserStepComponent = memo<SuperuserStepProps>(props => {
  const { t } = useTranslation(Namespaces.Setup);
  const classes = useSetupPageStyles();
  const { auth, onUserCreate } = props;
  const dispatch = useDispatch();

  const [state, setState] = useObjectState<State>({
    isFetching: false,
    errorMessage: undefined,
    apiErrors: {},
    values: {
      email: '',
      firstName: '',
      language: 'en',
      lastName: '',
      password: '',
      confirmPassword: '',
    },
  });
  const { values, apiErrors, errorMessage, isFetching } = state;

  const submit = async (values: SignUpPageFormValues) => {
    const data = {
      email: values.email.trim(),
      password: values.password.trim(),
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      language: values.language.trim() as Language,
    };
    try {
      setState({ isFetching: true, apiErrors: {}, errorMessage: undefined });
      const token = await AuthenticationApiService.register(data);
      dispatch(authorizeUserByToken(token.data.payload.accessToken, new Date(token.data.payload.expires)));
      setState({ isFetching: false });
      onUserCreate();
    } catch (e) {
      const error = e as ApiClientError;
      console.error(error);
      if (error.response?.data.message === 'unprocessable_entity') {
        const payload: UnprocessableEntityPayload<keyof LoginPageFormValues> = error.response.data.payload;
        setState({
          apiErrors: unprocessibleEntityToObject(payload),
          isFetching: false,
        });
      } else {
        setState({
          isFetching: false,
          errorMessage: error.response?.data.message,
        });
      }
    }
  };

  const resetApiError: ResetApiErrorForField = name => {
    if (apiErrors[name]) {
      setState({ apiErrors: { ...apiErrors, [name]: undefined } });
    }
  };

  return (
    <Grid container className={classes.root}>
      <Grid item xs={6} className={classes.description}>
        <Typography variant="h1" data-cy="setup-superuser-form-title">
          {t('page.superuser.title')}
        </Typography>
        <Typography variant="body1">{t('page.superuser.description')}</Typography>
      </Grid>
      <Grid item xs={6} className={classes.formWrapper}>
        <Formik initialValues={values} validationSchema={validationSchema} onSubmit={submit}>
          {props => (
            <div className={classes.form}>
              <SignUpUserForm
                {...props}
                isFetching={isFetching || auth.isFetching}
                apiErrors={apiErrors}
                resetApiError={resetApiError}
                actions={
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    size="small"
                    disabled={isFetching}
                    data-cy="setup-superuser-form-submit"
                    fullWidth
                  >
                    {t('page.buttons.submit')}
                  </Button>
                }
              />
              <div className={classes.errorBlock} data-cy="setup-superuser-form-error">
                {errorMessage && errorMessage !== 'no_invitation' && (
                  <p className={classes.errorLine}>
                    {t([
                      `${Namespaces.Login}:page.login.error.${errorMessage}`,
                      `${Namespaces.Login}:page.login.error.uncaught_error`,
                    ])}
                  </p>
                )}
              </div>
            </div>
          )}
        </Formik>
      </Grid>
    </Grid>
  );
});

export const SuperuserStep = connector(SuperuserStepComponent);
